import { apiClient } from "../common/apiClient";

// Development mode flag
const isDevelopment = !import.meta.env.PROD;

// Interface for the enhanced response
interface SalesforceQueryResponse<T> {
  records: T[];
  serviceUsed: "THAIFORCE" | "Salesforce";
  fallbackUsed: boolean;
  thaiforceError?: string;
  timestamp: string;
}

/**
 * Main query function to access Salesforce data through the backend API
 * Now uses hybrid THAIFORCE + Salesforce fallback system
 * @param soql The SOQL query to execute
 * @returns Enhanced response with records and service metadata
 */
export async function querySalesforce<T>(
  soql: string
): Promise<SalesforceQueryResponse<T>> {
  try {
    const data = await apiClient.post<SalesforceQueryResponse<T>>(
      "/api/salesforce/query",
      { soql }
    );

    console.log("Salesforce query response:", data);

    // Check if response has the new format with metadata
    if (data && typeof data === "object" && "serviceUsed" in data) {
      return data;
    }

    // Fallback for old format (just records array)
    return {
      records: (data as any) || [],
      serviceUsed: "Salesforce",
      fallbackUsed: false,
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    console.error("Failed to query Salesforce:", error);

    // Provide more detailed logging in development mode
    if (isDevelopment) {
      const apiError = error as any;
      if (apiError.status) {
        console.error(
          `Server responded with status ${apiError.status}:`,
          apiError.message
        );

        // Handle rate limiting specifically
        if (apiError.status === 429) {
          throw new Error("Rate limit exceeded. Please try again later.");
        }
      }
    }

    throw new Error("Failed to query Salesforce data");
  }
}

/**
 * Legacy function that returns just the records (for backward compatibility)
 * @param soql The SOQL query to execute
 * @returns Array of query results
 */
export async function querySalesforceRecords<T>(soql: string): Promise<T[]> {
  const response = await querySalesforce<T>(soql);
  return response.records;
}

/**
 * Get the status of both THAIFORCE and Salesforce services
 * @returns Service status information
 */
export async function getServiceStatus(): Promise<{
  thaiforce: { available: boolean; error: string | null };
  salesforce: { available: boolean; error: string | null };
}> {
  try {
    return await apiClient.get("/api/salesforce/status");
  } catch (error) {
    console.error("Failed to get service status:", error);
    throw new Error("Failed to get service status");
  }
}

/**
 * Check if THAIFORCE is currently available
 * @returns True if THAIFORCE is available
 */
export async function isThaiforceAvailable(): Promise<boolean> {
  try {
    const status = await getServiceStatus();
    return status.thaiforce.available;
  } catch (error) {
    console.error("Failed to check THAIFORCE availability:", error);
    return false;
  }
}
